import { useConfigStore } from '@/Stores/config';
import { useContextStore } from '@/Stores/context';
import type { PagesConfig } from '@shared/types/PagesConfig';

const THEMES = [
  'base',
  'conversion',
  'container',
  'custom-1',
  'custom-2',
  'custom-3',
  'custom-4',
] as const;
const COLOR_TOKENS = ['background-color', 'color', 'link-color', 'link-color-hover'] as const;

const ALLOWED_IDS = new Set(
  THEMES.flatMap((t) => COLOR_TOKENS.map((k) => `color-scheme-${t}-${k}`)),
);

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export const COLOR_FALLBACK_MAP: Record<string, string> = {
  'color-scheme-base-background-color': '#FFF',
  'color-scheme-base-color': '#000',
  'color-scheme-base-link-color': '#0A6156',
  'color-scheme-base-link-color-hover': '#0A3E37',

  'color-scheme-conversion-background-color': '#93C01C',
  'color-scheme-conversion-color': '#000',
  'color-scheme-conversion-link-color': '#000',
  'color-scheme-conversion-link-color-hover': '#000',

  'color-scheme-container-background-color': '#CCECE8',
  'color-scheme-container-color': '#000',
  'color-scheme-container-link-color': '#0A6156',
  'color-scheme-container-link-color-hover': '#0A3E37',

  'color-scheme-custom-1-background-color': '#FFF',
  'color-scheme-custom-1-color': '#000',
  'color-scheme-custom-1-link-color': '#0A6156',
  'color-scheme-custom-1-link-color-hover': '#0A3E37',

  'color-scheme-custom-2-background-color': '#FFF',
  'color-scheme-custom-2-color': '#000',
  'color-scheme-custom-2-link-color': '#0A6156',
  'color-scheme-custom-2-link-color-hover': '#0A3E37',

  'color-scheme-custom-3-background-color': '#FFF',
  'color-scheme-custom-3-color': '#000',
  'color-scheme-custom-3-link-color': '#0A6156',
  'color-scheme-custom-3-link-color-hover': '#0A3E37',

  'color-scheme-custom-4-background-color': '#FFF',
  'color-scheme-custom-4-color': '#000',
  'color-scheme-custom-4-link-color': '#0A6156',
  'color-scheme-custom-4-link-color-hover': '#0A3E37',
};

type ColorItem = { id?: string; value?: unknown };

function buildCssVariables(mode: 'standalone' | 'wc', list?: ColorItem[]): string {
  const selector = mode === 'wc' ? ':host' : ':root';

  const decls: string[] = [];
  const seen = new Set<string>();

  if (Array.isArray(list) && list.length > 0) {
    for (const c of list) {
      if (!c?.id || c.value == null) continue;

      const id = String(c.id).trim();

      if (!ALLOWED_IDS.has(id)) continue;

      const raw = String(c.value)
        .replace(/[\r\n]+/g, ' ')
        .trim();
      if (!HEX_COLOR.test(raw)) continue;

      if (seen.has(id)) continue;
      seen.add(id);

      decls.push(`  --${id}: ${raw};`);
    }
  }

  for (const [id, fallbackValue] of Object.entries(COLOR_FALLBACK_MAP)) {
    if (!seen.has(id)) {
      decls.push(`  --${id}: ${fallbackValue};`);
    }
  }

  return `${selector} {\n${decls.join('\n')}\n}`;
}

export function cssVariablesFromConfig(mode: 'standalone' | 'wc' = 'standalone'): string {
  const cfgStore = useConfigStore();
  return buildCssVariables(mode, cfgStore.config?.color_settings?.custom_color_list);
}

export function cssVariablesFromFallbacks(mode: 'standalone' | 'wc' = 'standalone'): string {
  return buildCssVariables(mode);
}

export function applyFaviconsFromConfig(): void {
  const cfgStore = useConfigStore();
  const list = (cfgStore.config as PagesConfig)?.favicon_list ?? [];

  if (!Array.isArray(list) || list.length === 0) {
    return;
  }

  const selectors = [
    'link[rel="icon"]',
    'link[rel="shortcut icon"]',
    'link[rel="apple-touch-icon"]',
  ];
  document.head
    .querySelectorAll<HTMLLinkElement>(selectors.join(','))
    .forEach((el) => el.parentNode?.removeChild(el));

  for (const item of list) {
    const href = item?.value?.trim();
    if (!href) continue;

    const rawRel = String(item?.rel ?? '')
      .trim()
      .toLowerCase();
    if (!rawRel) continue;

    const rel = rawRel === 'shortcut' ? 'shortcut icon' : rawRel;

    const link = document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('href', href);
    document.head.appendChild(link);

    if (rel === 'icon') {
      const appleLink = document.createElement('link');
      appleLink.setAttribute('rel', 'apple-touch-icon');
      appleLink.setAttribute('href', href);
      document.head.appendChild(appleLink);
    }
  }
}

export function applyPwaManifestFromConfig(): void {
  const cfgStore = useConfigStore();
  const contextStore = useContextStore();
  const config = cfgStore.config as PagesConfig;

  const getPwaName = (): string => {
    const title = config?.portal_settings?.title_list?.[0]?.value;
    return (typeof title === 'string' && title.trim()) || 'Pages PWA';
  };

  const getThemeColor = (): string => {
    const color = config?.color_settings?.hyperlink?.color;
    return (typeof color === 'string' && color.trim()) || '#ffffff';
  };

  const buildIconUrls = (
    href: string,
  ): Array<{ src: string; sizes: string; type: 'image/png' }> => {
    const encodedUrl = encodeURIComponent(href);
    const base = 'https://img.destination.one/remote/.png?mode=crop&scale=both&format=png';
    return [
      {
        src: `${base}&width=192&height=192&url=${encodedUrl}`,
        sizes: '192x192',
        type: 'image/png',
      },
      {
        src: `${base}&width=512&height=512&url=${encodedUrl}`,
        sizes: '512x512',
        type: 'image/png',
      },
    ];
  };

  const existing = document.head.querySelector('link[rel="manifest"]');
  if (existing?.parentNode) {
    existing.parentNode.removeChild(existing);
  }

  const pwaName = getPwaName();
  const themeColor = getThemeColor();

  const pwaUrl = `${window.origin}/${contextStore.language}/${contextStore.experience}/${contextStore.template}/`;
  const manifest = {
    name: pwaName,
    short_name: pwaName.slice(0, 12),
    start_url: `${pwaUrl}portal`,
    scope: pwaUrl,
    display: 'standalone',
    background_color: themeColor,
    theme_color: themeColor,
    icons: [] as Array<{ src: string; sizes: string; type: 'image/png' }>,
  };

  const faviconList = config?.favicon_list ?? [];
  if (Array.isArray(faviconList) && faviconList.length > 0) {
    const iconItems = faviconList.filter(
      (item) =>
        String(item?.rel ?? '')
          .trim()
          .toLowerCase() === 'icon',
    );

    for (const item of iconItems) {
      const href = String(item?.value ?? '').trim();
      if (!href) continue;
      manifest.icons.push(...buildIconUrls(href));
    }
  }

  const manifestJson = JSON.stringify(manifest);
  const dataUrl = `data:application/json;charset=utf-8,${encodeURIComponent(manifestJson)}`;

  const link = document.createElement('link');
  link.rel = 'manifest';
  link.href = dataUrl;
  document.head.appendChild(link);
}
